(() => {
  const STORAGE_KEY = 'soundhousCompareV1';
  const AMP_MULTI_HANDLE = 'sonos-amp-multi';
  const AUDIO_COMPONENT_COLLECTION = 'amplifiers';
  const AUDIO_COMPONENT_ORDER = ['sonos-amp', 'sonos-amp-multi', 'port'];

  const getHandleFromCard = (card) => {
    const link = card?.querySelector('a[href*="/products/"]');
    const href = link?.getAttribute('href') || '';
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const parseRegistry = (root) => {
    if (!root) return [];
    try {
      const registry = JSON.parse(root.querySelector('[data-shcmp-registry]')?.textContent || '[]');
      return Array.isArray(registry) ? registry : [];
    } catch (error) {
      return [];
    }
  };

  const loadSelection = () => {
    try {
      const selection = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(selection) ? selection.filter((product) => product?.handle && product?.profile) : [];
    } catch (error) {
      return [];
    }
  };

  const cardWrappers = () => [...document.querySelectorAll('.product-card-wrapper, .card-wrapper')];

  const hideRetailControls = (card) => {
    card.querySelectorAll('.quick-add').forEach((element) => {
      element.hidden = true;
      element.style.setProperty('display', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
    });

    card.querySelectorAll('.badge').forEach((badge) => {
      if ((badge.textContent || '').trim().toLowerCase() !== 'sold out') return;
      badge.hidden = true;
      badge.style.setProperty('display', 'none', 'important');
      badge.setAttribute('aria-hidden', 'true');
    });
  };

  const createProfessionalState = (compare = false) => {
    const state = document.createElement('div');
    state.className = `sh-professional-card-state${compare ? ' sh-professional-card-state--compare' : ''}`;
    state.setAttribute('aria-label', 'Pre-order, professional installation');
    state.innerHTML = '<span>Pre-order</span><span class="sh-professional-card-state__dot" aria-hidden="true">·</span><span>Professional installation</span>';
    return state;
  };

  const addProfessionalState = (card) => {
    if (card.querySelector('.sh-professional-card-state')) return;

    const price = card.querySelector('.price-with-swatch, .price');
    const cardInfo = card.querySelector('.card-information, .card__information');
    const state = createProfessionalState(false);

    if (price) {
      price.insertAdjacentElement('afterend', state);
    } else if (cardInfo) {
      cardInfo.appendChild(state);
    } else {
      card.appendChild(state);
    }
  };

  const professionalHandlesFor = (root) => {
    const handles = new Set([AMP_MULTI_HANDLE]);
    parseRegistry(root)
      .filter((product) => product?.professionalOnly)
      .forEach((product) => handles.add(product.handle));
    return handles;
  };

  const applyProfessionalCards = (root = null) => {
    const professionalHandles = professionalHandlesFor(root);

    cardWrappers().forEach((card) => {
      const handle = getHandleFromCard(card);
      if (!professionalHandles.has(handle)) return;
      hideRetailControls(card);
      addProfessionalState(card);
      card.dataset.shProfessionalOnly = 'true';
    });
  };

  const reorderAudioComponents = (root) => {
    if (!root || root.dataset.shcmpCollectionHandle !== AUDIO_COMPONENT_COLLECTION) return;

    const itemByHandle = new Map();
    cardWrappers().forEach((card) => {
      const handle = getHandleFromCard(card);
      if (!AUDIO_COMPONENT_ORDER.includes(handle)) return;
      const item = card.closest('li.grid__item, .grid__item') || card.parentElement;
      if (item) itemByHandle.set(handle, item);
    });

    const availableHandles = AUDIO_COMPONENT_ORDER.filter((handle) => itemByHandle.has(handle));
    if (availableHandles.length < 2) return;

    const itemsInDomOrder = [...new Set(availableHandles.map((handle) => itemByHandle.get(handle)))]
      .sort((a, b) => {
        if (a === b) return 0;
        return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });

    const currentHandles = itemsInDomOrder.map((item) =>
      getHandleFromCard(item.querySelector('.product-card-wrapper, .card-wrapper') || item)
    );
    if (currentHandles.join('|') === availableHandles.join('|')) return;

    const firstItem = itemsInDomOrder[0];
    const parent = firstItem?.parentElement;
    if (!parent) return;
    if (!itemsInDomOrder.every((item) => item.parentElement === parent)) return;

    const marker = document.createComment('soundhous-audio-components-order');
    parent.insertBefore(marker, firstItem);

    const fragment = document.createDocumentFragment();
    availableHandles.forEach((handle) => fragment.appendChild(itemByHandle.get(handle)));
    parent.insertBefore(fragment, marker.nextSibling);
    marker.remove();
  };

  const syncCompareProfessionalState = (root) => {
    if (!root) return;
    const selection = loadSelection();
    const cells = [...root.querySelectorAll('[data-shcmp-showcase] .shcmp-product-showcase')];
    if (!cells.length) return;

    cells.forEach((cell, index) => {
      const selected = selection[index];
      const professional = selected?.professionalOnly || selected?.handle === AMP_MULTI_HANDLE;
      const existing = cell.querySelector('.sh-professional-card-state--compare');

      if (!professional) {
        existing?.remove();
        return;
      }
      if (existing) return;

      const state = createProfessionalState(true);
      const price = cell.querySelector('.shcmp-product-showcase__price');
      if (price) price.insertAdjacentElement('afterend', state);
      else cell.prepend(state);
    });
  };

  const patchAudioComponentFallbacks = (root) => {
    if (!root) return;
    const selection = loadSelection().filter((product) => product?.profile?.family === 'audio_component');
    const portIndex = selection.findIndex((product) => product.handle === 'port');
    if (portIndex < 0) return;

    root.querySelectorAll('.shcmp-data-row').forEach((row) => {
      const label = (row.querySelector('.shcmp-label')?.textContent || '').trim();
      const cell = row.querySelectorAll('.shcmp-value')[portIndex];
      if (!cell) return;

      if (label === 'Line output' && (!cell.textContent.trim() || cell.textContent.trim() === '—')) {
        cell.textContent = 'Stereo RCA analogue line-out + digital coaxial output';
      }

      if (label === '12V trigger' && (!cell.textContent.trim() || cell.textContent.trim() === '—')) {
        cell.textContent = '12V trigger for compatible external amplifiers';
      }
    });
  };

  const autoOpenCompare = (root) => {
    if (!root || root.dataset.shcmpAutoOpenHandled === 'true') return;

    let requested = false;
    try {
      requested = new URLSearchParams(window.location.search).get('soundhous_compare') === 'open';
    } catch (error) {
      requested = false;
    }
    if (!requested) return;

    let attempts = 0;
    const tryOpen = () => {
      if (root.dataset.shcmpAutoOpenHandled === 'true') return;
      attempts += 1;

      const button = root.querySelector('[data-shcmp-open]');
      if (button && !button.disabled) {
        root.dataset.shcmpAutoOpenHandled = 'true';
        button.click();
        return;
      }

      if (attempts < 30) window.setTimeout(tryOpen, 100);
    };

    window.setTimeout(tryOpen, 0);
  };

  const apply = (root = null) => {
    applyProfessionalCards(root);
    if (root) {
      reorderAudioComponents(root);
      syncCompareProfessionalState(root);
      patchAudioComponentFallbacks(root);
      autoOpenCompare(root);
    }
  };

  const bindRoot = (root) => {
    if (!root || root.dataset.shProfessionalCardV1Ready === 'true') return;
    root.dataset.shProfessionalCardV1Ready = 'true';

    let timer;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => apply(root), 50);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();
  };

  const bindStandalone = () => {
    if (document.documentElement.dataset.shProfessionalStandaloneReady === 'true') return;
    document.documentElement.dataset.shProfessionalStandaloneReady = 'true';

    let timer;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => applyProfessionalCards(null), 50);
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();
  };

  const init = () => {
    const roots = [...document.querySelectorAll('[data-soundhous-compare-root]')];
    if (roots.length) {
      roots.forEach(bindRoot);
    } else {
      bindStandalone();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();